import { useState } from "react";
import { Fingerprint, Lock } from "lucide-react";
import { StarLogo } from "./StarLogo";
import { toast } from "sonner";
import { useVault } from "@/context/VaultContext";
import { PinPad } from "./PinPad";
import { verifyBiometric } from "@/lib/lock";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="lock-screen">
      <div className="p-6 flex items-center gap-2">
        <StarLogo className="h-6 w-6" />
        <span className="font-heading font-bold tracking-tight">StarVault</span>
        <span className="text-muted-foreground text-sm">• by Sammy Star</span>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="sv-rise w-full max-w-sm rounded-xl border bg-card/90 backdrop-blur-xl p-8 shadow-2xl">
          <div className="mx-auto mb-6 h-14 w-14 rounded-full border border-primary/40 flex items-center justify-center sv-pulse">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function SetupPin() {
  const { createPin, setDialog } = useVault();
  const [first, setFirst] = useState(null);
  const [error, setError] = useState("");
  const done = async (pin) => {
    if (!first) { setFirst(pin); setError(""); return true; }
    if (pin !== first) { setFirst(null); setError("PINs didn't match — start again"); return false; }
    await createPin(pin);
    setDialog("profile");
    toast.success("Vault created", { description: "Set up your Creator Profile now, or close to skip." });
    return true;
  };
  return (
    <PinPad key={first ? "confirm" : "enter"} testPrefix="setup-pin" error={error} onComplete={done}
      title={first ? "Confirm your PIN" : "Create a 4-digit PIN"}
      subtitle={first ? "Enter the same PIN again" : "There is no PIN recovery — keep it safe."} />
  );
}

function Unlock() {
  const { security, unlockWithPin, unlockWithBio } = useVault();
  const [error, setError] = useState("");
  const done = async (pin) => {
    const err = await unlockWithPin(pin);
    setError(err || "");
    return !err;
  };
  const bio = async () => {
    try { await verifyBiometric(security.bioId); await unlockWithBio(); }
    catch (e) { toast.error("Biometric unlock failed — use your PIN", { description: e.message }); }
  };
  return (
    <div className="flex flex-col items-center gap-6">
      <PinPad testPrefix="unlock-pin" title="Vault Locked" subtitle="Enter your PIN to unlock" error={error} onComplete={done} />
      {security.bioId && (
        <button type="button" onClick={bio} data-testid="biometric-unlock-button"
          className="flex items-center gap-2 rounded-full border border-primary/40 px-5 py-2 text-sm text-primary hover:bg-primary/10 transition-colors">
          <Fingerprint className="h-4 w-4" /> Unlock with fingerprint
        </button>
      )}
    </div>
  );
}

export default function Gate() {
  const { security } = useVault();
  return <Shell>{security.wrapped || security.pinHash ? <Unlock /> : <SetupPin />}</Shell>;
}
